import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, CalendarDays, CheckCircle2, Circle, DollarSign } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SERVICE_LABELS = {
  carpet: "Carpet Cleaning",
  upholstery: "Upholstery & Rug Cleaning",
  detailing: "Mobile Car Detailing",
  landscaping: "Landscaping & Lawn Care",
  maid: "Maid & House Cleaning",
  pressure: "Pressure Washing",
  windows: "Window & Gutter Cleaning",
  multiple: "Multiple Services",
};

const auth = (token) => ({ headers: { Authorization: `Bearer ${token}` } });

const useCollection = (path, token) => {
  const [items, setItems] = useState([]);
  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}${path}`, auth(token));
      setItems(data);
    } catch (e) {
      console.error(e);
    }
  }, [path, token]);
  useEffect(() => { load(); }, [load]);
  return [items, load];
};

const InlineForm = ({ fields, onSubmit, submitLabel, testid, initial }) => {
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (initial) setForm(initial);
  }, [initial]);
  const handle = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await onSubmit(form);
      setForm({});
    } finally {
      setBusy(false);
    }
  };
  return (
    <form className="ops-form" onSubmit={handle} data-testid={testid}>
      {fields.map((f) =>
        f.type === "select" ? (
          <select key={f.name} required={f.required !== false} value={form[f.name] || ""} onChange={(e) => setForm({ ...form, [f.name]: e.target.value })} data-testid={`${testid}-${f.name}`}>
            <option value="" disabled>{f.placeholder}</option>
            {f.options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        ) : (
          <input key={f.name} type={f.type || "text"} step={f.step} placeholder={f.placeholder} required={f.required !== false} value={form[f.name] || ""} onChange={(e) => setForm({ ...form, [f.name]: e.target.value })} data-testid={`${testid}-${f.name}`} />
        )
      )}
      <button type="submit" className="submit-btn ops-submit" disabled={busy} data-testid={`${testid}-submit`}>{busy ? "SAVING..." : submitLabel}</button>
    </form>
  );
};

const StatusPill = ({ label, tone }) => (
  <span className={`status-pill status-${tone}`}>{label}</span>
);

const gcalLink = (b) => {
  const dateClean = (b.date || "").replace(/-/g, "");
  const timeClean = (b.time || "09:00").replace(":", "") + "00";
  const endHour = String((parseInt((b.time || "09:00").slice(0, 2), 10) + 2) % 24).padStart(2, "0") + (b.time || "09:00").slice(3, 5) + "00";
  const details = [`Customer: ${b.customer_name}`, b.phone ? `Phone: ${b.phone}` : "", b.email ? `Email: ${b.email}` : "", b.notes ? `Notes: ${b.notes}` : "", "Booked via Epic Expectations Back Office"].filter(Boolean).join("\n");
  return `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(`Epic: ${SERVICE_LABELS[b.service] || b.service} — ${b.customer_name}`)}&dates=${dateClean}T${timeClean}/${dateClean}T${endHour}&details=${encodeURIComponent(details)}`;
};

const bookingTone = (s) => (s === "completed" ? "green" : s === "cancelled" ? "pink" : s === "rescheduled" ? "pink" : "blue");

export const BookingsTab = ({ token, prefill }) => {
  const [items, load] = useCollection("/admin/bookings", token);
  const [archivedItems, loadArchived] = useCollection("/admin/bookings?archived=true", token);
  const [showArchived, setShowArchived] = useState(false);
  const [cursor, setCursor] = useState(() => { const d = new Date(); return { y: d.getFullYear(), m: d.getMonth() }; });
  const [selectedDay, setSelectedDay] = useState(null);

  const create = async (form) => {
    await axios.post(`${API}/admin/bookings`, form, auth(token));
    load();
  };
  const patch = async (id, body) => {
    await axios.patch(`${API}/admin/bookings/${id}`, body, auth(token));
    load();
    loadArchived();
  };
  const reschedule = (b) => {
    const date = window.prompt("New date (YYYY-MM-DD):", b.date);
    if (!date) return;
    const time = window.prompt("New time (HH:MM, 24h):", b.time || "09:00") || b.time;
    patch(b.id, { status: "rescheduled", date, time });
  };

  const byDate = {};
  items.forEach((b) => { (byDate[b.date] = byDate[b.date] || []).push(b); });

  const first = new Date(cursor.y, cursor.m, 1);
  const daysInMonth = new Date(cursor.y, cursor.m + 1, 0).getDate();
  const blanks = first.getDay();
  const cells = [...Array(blanks).fill(null), ...Array.from({ length: daysInMonth }, (_, i) => i + 1)];
  const monthName = first.toLocaleString("default", { month: "long", year: "numeric" });
  const move = (delta) => setCursor(({ y, m }) => { const d = new Date(y, m + delta, 1); return { y: d.getFullYear(), m: d.getMonth() }; });
  const dayBookings = selectedDay ? (byDate[selectedDay] || []) : [];
  const listBookings = selectedDay ? dayBookings : items;

  return (
    <div data-testid="bookings-tab">
      <InlineForm testid="booking-form" submitLabel="SCHEDULE BOOKING" onSubmit={create} initial={prefill} fields={[
        { name: "customer_name", placeholder: "Customer Name" },
        { name: "phone", placeholder: "Phone", required: false },
        { name: "service", type: "select", placeholder: "Service...", options: Object.entries(SERVICE_LABELS).map(([value, label]) => ({ value, label })) },
        { name: "date", type: "date", placeholder: "Date" },
        { name: "time", type: "time", placeholder: "Time", required: false },
        { name: "notes", placeholder: "Notes (optional)", required: false },
      ]} />
      <div className="cal-shell" data-testid="bookings-calendar">
        <div className="cal-header">
          <button onClick={() => move(-1)} data-testid="cal-prev">&lt;</button>
          <h3 className="neon-text-blue" data-testid="cal-month">{monthName}</h3>
          <button onClick={() => move(1)} data-testid="cal-next">&gt;</button>
        </div>
        <div className="cal-grid cal-dow">
          {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => <span key={d}>{d}</span>)}
        </div>
        <div className="cal-grid">
          {cells.map((day, i) => {
            const key = day ? `${cursor.y}-${String(cursor.m + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}` : null;
            const count = key ? (byDate[key] || []).length : 0;
            return (
              <button
                key={i}
                className={`cal-cell ${day ? "" : "cal-blank"} ${count ? "cal-has" : ""} ${selectedDay === key ? "cal-selected" : ""}`}
                disabled={!day}
                onClick={() => setSelectedDay(selectedDay === key ? null : key)}
                data-testid={key ? `cal-day-${key}` : `cal-blank-${i}`}
              >
                {day}
                {count > 0 && <span className="cal-dot">{count}</span>}
              </button>
            );
          })}
        </div>
        {selectedDay && (
          <p className="cal-filter-note" data-testid="cal-filter-note">
            Showing {selectedDay} — <button onClick={() => setSelectedDay(null)} data-testid="cal-clear-filter">show all</button>
          </p>
        )}
      </div>
      <div className="archive-toggle">
        <button className="admin-tab" onClick={() => setShowArchived(!showArchived)} data-testid="bookings-archive-toggle">
          {showArchived ? "Hide Archived" : `Show Archived (${archivedItems.length})`}
        </button>
      </div>
      <div className="admin-leads">
        {(showArchived ? archivedItems : listBookings).map((b) => (
          <div className="lead-card" key={b.id} data-testid={`booking-card-${b.id}`}>
            <div className="lead-top">
              <span className="lead-service">{SERVICE_LABELS[b.service] || b.service}</span>
              <span>
                <StatusPill label={b.status.toUpperCase()} tone={bookingTone(b.status)} />{" "}
                {b.paid && <StatusPill label="PAID" tone="green" />}
              </span>
            </div>
            <h3>{b.customer_name}</h3>
            <div className="lead-contacts">
              <span className="lead-contact"><CalendarDays size={15} /> {b.date}{b.time ? ` ${b.time}` : ""}</span>
              {b.phone && <a href={`tel:${b.phone}`} className="lead-contact"><Phone size={15} /> {b.phone}</a>}
            </div>
            {b.notes && <p className="lead-details">{b.notes}</p>}
            <div className="ops-actions">
              <a href={gcalLink(b)} target="_blank" rel="noopener noreferrer" className="gcal-link" data-testid={`booking-gcal-${b.id}`}>+ Google Calendar</a>
              {(b.status === "scheduled" || b.status === "rescheduled") && <button onClick={() => reschedule(b)} data-testid={`booking-reschedule-${b.id}`}>Reschedule</button>}
              {(b.status === "scheduled" || b.status === "rescheduled") && <button onClick={() => patch(b.id, { status: "completed" })} data-testid={`booking-complete-${b.id}`}>Mark Completed</button>}
              {!b.paid && b.status !== "cancelled" && <button onClick={() => patch(b.id, { paid: true })} data-testid={`booking-paid-${b.id}`}>Mark Paid</button>}
              {b.status !== "cancelled" && <button onClick={() => patch(b.id, { status: "cancelled" })} data-testid={`booking-cancel-${b.id}`}>Cancel</button>}
              {!b.archived && <button onClick={() => patch(b.id, { archived: true })} data-testid={`booking-archive-${b.id}`}>Archive</button>}
              {b.archived && <button onClick={() => patch(b.id, { archived: false })} data-testid={`booking-unarchive-${b.id}`}>Restore</button>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export const JobOrdersTab = ({ token }) => {
  const [items, load] = useCollection("/admin/job-orders", token);
  const create = async (form) => {
    await axios.post(`${API}/admin/job-orders`, { ...form, amount: parseFloat(form.amount) }, auth(token));
    load();
  };
  const setPaid = async (id, paid) => {
    await axios.patch(`${API}/admin/job-orders/${id}`, { paid }, auth(token));
    load();
  };
  const unpaid = items.filter((i) => !i.paid).reduce((s, i) => s + i.amount, 0);
  return (
    <div data-testid="joborders-tab">
      <p className="ops-total" data-testid="joborders-outstanding">Outstanding: <span className="neon-text-pink">${unpaid.toFixed(2)}</span></p>
      <InlineForm testid="joborder-form" submitLabel="CREATE JOB ORDER" onSubmit={create} fields={[
        { name: "customer_name", placeholder: "Customer Name" },
        { name: "email", type: "email", placeholder: "Email (links to customer)", required: false },
        { name: "service", type: "select", placeholder: "Service...", options: Object.entries(SERVICE_LABELS).map(([value, label]) => ({ value, label })) },
        { name: "amount", type: "number", step: "0.01", placeholder: "Amount ($)" },
        { name: "note", placeholder: "Note (optional)", required: false },
      ]} />
      <div className="admin-leads">
        {items.map((o) => (
          <div className="lead-card" key={o.id} data-testid={`joborder-card-${o.id}`}>
            <div className="lead-top">
              <span className="lead-service">{SERVICE_LABELS[o.service] || o.service}</span>
              <StatusPill label={o.paid ? "PAID" : "UNPAID"} tone={o.paid ? "green" : "pink"} />
            </div>
            <h3>{o.customer_name}</h3>
            <p className="ops-amount"><DollarSign size={16} /> {o.amount.toFixed(2)}</p>
            {o.note && <p className="lead-details">{o.note}</p>}
            <div className="ops-actions">
              {!o.paid && <button onClick={() => setPaid(o.id, true)} data-testid={`joborder-paid-${o.id}`}>Mark Paid</button>}
              {o.paid && <button onClick={() => setPaid(o.id, false)} data-testid={`joborder-unpaid-${o.id}`}>Reopen</button>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export const CustomersTab = ({ token }) => {
  const [items] = useCollection("/admin/customers", token);
  return (
    <div data-testid="customers-tab">
      <div className="admin-leads">
        {items.map((c) => (
          <div className="lead-card" key={c.email} data-testid={`customer-card-${c.email}`}>
            <div className="lead-top">
              <span className="lead-service">{c.requests} quote request{c.requests === 1 ? "" : "s"}</span>
              {c.job_orders > 0 && <StatusPill label={`${c.job_orders} job order${c.job_orders === 1 ? "" : "s"}`} tone="blue" />}
            </div>
            <h3>{c.name}</h3>
            <div className="lead-contacts">
              <a href={`mailto:${c.email}`} className="lead-contact"><Mail size={15} /> {c.email}</a>
              {c.phone && <a href={`tel:${c.phone}`} className="lead-contact"><Phone size={15} /> {c.phone}</a>}
            </div>
            <p className="lead-details">
              Services: {c.services.map((s) => SERVICE_LABELS[s] || s).join(", ") || "—"}
              {c.total_billed ? ` · Billed: $${c.total_billed.toFixed(2)}` : ""}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
};

export const ApplicationsTab = ({ token }) => {
  const [items, load] = useCollection("/admin/applications", token);
  const [archivedItems, loadArchived] = useCollection("/admin/applications?archived=true", token);
  const [showArchived, setShowArchived] = useState(false);
  const patchApp = async (id, body) => {
    await axios.patch(`${API}/admin/applications/${id}`, body, auth(token));
    load();
    loadArchived();
  };
  const scheduleInterview = (a) => {
    const date = window.prompt("Interview date (YYYY-MM-DD):", a.interview_date || "");
    if (!date) return;
    const time = window.prompt("Interview time (HH:MM):", a.interview_time || "10:00") || "";
    const notes = window.prompt("Interview notes (location, video link, etc.):", a.interview_notes || "") || "";
    patchApp(a.id, { status: "interviewing", interview_date: date, interview_time: time, interview_notes: notes });
  };
  const appTone = (s) => (s === "hired" ? "green" : s === "rejected" ? "pink" : s === "interviewing" ? "pink" : "blue");
  return (
    <div data-testid="applications-tab">
      <div className="archive-toggle">
        <button className="admin-tab" onClick={() => setShowArchived(!showArchived)} data-testid="applications-archive-toggle">
          {showArchived ? "Hide Archived" : `Show Archived (${archivedItems.length})`}
        </button>
      </div>
      <div className="admin-leads">
        {items.length === 0 && !showArchived && <p className="ops-empty">No applications yet. Share the Careers page to recruit.</p>}
        {(showArchived ? archivedItems : items).map((a) => (
          <div className="lead-card" key={a.id} data-testid={`application-card-${a.id}`}>
            <div className="lead-top">
              <span className="lead-service">{a.position}</span>
              <StatusPill label={a.status.toUpperCase()} tone={appTone(a.status)} />
            </div>
            <h3>{a.name}</h3>
            <div className="lead-contacts">
              <a href={`tel:${a.phone}`} className="lead-contact"><Phone size={15} /> {a.phone}</a>
              <a href={`mailto:${a.email}`} className="lead-contact"><Mail size={15} /> {a.email}</a>
            </div>
            {a.message && <p className="lead-details">{a.message}</p>}
            {a.status === "interviewing" && a.interview_date && (
              <p className="lead-details interview-line" data-testid={`interview-info-${a.id}`}>
                <CalendarDays size={15} /> Interview: {a.interview_date}{a.interview_time ? ` ${a.interview_time}` : ""}{a.interview_notes ? ` — ${a.interview_notes}` : ""}
              </p>
            )}
            <div className="ops-actions">
              {a.status !== "hired" && a.status !== "rejected" && <button onClick={() => scheduleInterview(a)} data-testid={`application-interview-${a.id}`}>{a.status === "interviewing" ? "Edit Interview" : "Schedule Interview"}</button>}
              {a.status !== "hired" && <button onClick={() => patchApp(a.id, { status: "hired" })} data-testid={`application-hire-${a.id}`}>Hire</button>}
              {a.status === "new" && <button onClick={() => patchApp(a.id, { status: "reviewed" })} data-testid={`application-review-${a.id}`}>Mark Reviewed</button>}
              {a.status !== "rejected" && <button onClick={() => patchApp(a.id, { status: "rejected" })} data-testid={`application-reject-${a.id}`}>Reject</button>}
              {!a.archived && <button onClick={() => patchApp(a.id, { archived: true })} data-testid={`application-archive-${a.id}`}>Archive</button>}
              {a.archived && <button onClick={() => patchApp(a.id, { archived: false })} data-testid={`application-unarchive-${a.id}`}>Restore</button>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const ONBOARDING_STEPS = [
  ["id_verified", "ID Verified"],
  ["w4_signed", "W-4 Signed"],
  ["trained", "Trained"],
  ["uniform", "Uniform Issued"],
];

export const EmployeesTab = ({ token }) => {
  const [items, load] = useCollection("/admin/employees", token);
  const create = async (form) => {
    await axios.post(`${API}/admin/employees`, { ...form, pay_rate: parseFloat(form.pay_rate) }, auth(token));
    load();
  };
  const toggleStep = async (emp, key) => {
    await axios.patch(`${API}/admin/employees/${emp.id}`, { onboarding: { ...emp.onboarding, [key]: !emp.onboarding[key] } }, auth(token));
    load();
  };
  const toggleActive = async (emp) => {
    await axios.patch(`${API}/admin/employees/${emp.id}`, { active: !emp.active }, auth(token));
    load();
  };
  return (
    <div data-testid="employees-tab">
      <InlineForm testid="employee-form" submitLabel="ADD TO SQUAD" onSubmit={create} fields={[
        { name: "name", placeholder: "Full Name" },
        { name: "email", type: "email", placeholder: "Email", required: false },
        { name: "phone", placeholder: "Phone", required: false },
        { name: "role", type: "select", placeholder: "Role...", options: ["Carpet Cleaning Specialist", "Upholstery & Rug Specialist", "Mobile Car Detailer", "Landscaping & Lawn Crew", "Maid & House Cleaner", "Pressure Washing Tech", "Window & Gutter Tech", "Squad Leader"].map((r) => ({ value: r, label: r })) },
        { name: "pay_rate", type: "number", step: "0.01", placeholder: "Pay rate ($/hr)" },
      ]} />
      <div className="admin-leads">
        {items.map((e) => (
          <div className="lead-card" key={e.id} data-testid={`employee-card-${e.id}`}>
            <div className="lead-top">
              <span className="lead-service">{e.role}</span>
              <StatusPill label={e.active ? "ACTIVE" : "INACTIVE"} tone={e.active ? "green" : "pink"} />
            </div>
            <h3>{e.name} <span className="ops-rate">${e.pay_rate}/hr</span></h3>
            <div className="lead-contacts">
              {e.phone && <a href={`tel:${e.phone}`} className="lead-contact"><Phone size={15} /> {e.phone}</a>}
              {e.email && <a href={`mailto:${e.email}`} className="lead-contact"><Mail size={15} /> {e.email}</a>}
            </div>
            <div className="onboarding-row">
              {ONBOARDING_STEPS.map(([key, label]) => (
                <button key={key} className={`onboard-step ${e.onboarding[key] ? "done" : ""}`} onClick={() => toggleStep(e, key)} data-testid={`employee-${key}-${e.id}`}>
                  {e.onboarding[key] ? <CheckCircle2 size={14} /> : <Circle size={14} />} {label}
                </button>
              ))}
            </div>
            <div className="ops-actions">
              <button onClick={() => toggleActive(e)} data-testid={`employee-toggle-${e.id}`}>{e.active ? "Deactivate" : "Reactivate"}</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export const PayrollTab = ({ token, employees }) => {
  const [items, load] = useCollection("/admin/payroll", token);
  const create = async (form) => {
    const emp = employees.find((e) => e.id === form.employee_id);
    await axios.post(`${API}/admin/payroll`, {
      employee_id: form.employee_id,
      employee_name: emp ? emp.name : "Unknown",
      period: form.period,
      hours: parseFloat(form.hours),
      rate: parseFloat(form.rate),
      note: form.note || "",
    }, auth(token));
    load();
  };
  const setStatus = async (id, status) => {
    await axios.patch(`${API}/admin/payroll/${id}`, { status }, auth(token));
    load();
  };
  const pending = items.filter((i) => i.status === "pending").reduce((s, i) => s + i.amount, 0);
  return (
    <div data-testid="payroll-tab">
      <p className="ops-total" data-testid="payroll-pending">Pending payroll: <span className="neon-text-green">${pending.toFixed(2)}</span></p>
      <InlineForm testid="payroll-form" submitLabel="RUN PAYROLL ENTRY" onSubmit={create} fields={[
        { name: "employee_id", type: "select", placeholder: "Employee...", options: employees.map((e) => ({ value: e.id, label: `${e.name} ($${e.pay_rate}/hr)` })) },
        { name: "period", placeholder: "Period (e.g. Jan 1-15)" },
        { name: "hours", type: "number", step: "0.25", placeholder: "Hours" },
        { name: "rate", type: "number", step: "0.01", placeholder: "Rate ($/hr)" },
        { name: "note", placeholder: "Note (optional)", required: false },
      ]} />
      <div className="admin-leads">
        {items.map((p) => (
          <div className="lead-card" key={p.id} data-testid={`payroll-card-${p.id}`}>
            <div className="lead-top">
              <span className="lead-service">{p.period}</span>
              <StatusPill label={p.status.toUpperCase()} tone={p.status === "paid" ? "green" : "pink"} />
            </div>
            <h3>{p.employee_name}</h3>
            <p className="ops-amount"><DollarSign size={16} /> {p.amount.toFixed(2)} <span className="ops-rate">({p.hours}h x ${p.rate})</span></p>
            {p.note && <p className="lead-details">{p.note}</p>}
            <div className="ops-actions">
              {p.status === "pending" && <button onClick={() => setStatus(p.id, "paid")} data-testid={`payroll-paid-${p.id}`}>Mark Paid</button>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};


const SERVICE_CHECKBOXES = [
  "Carpet Cleaning",
  "Upholstery & Rug Cleaning",
  "Mobile Car Detailing",
  "Landscaping & Lawn Care",
  "Maid & House Cleaning",
  "Pressure Washing",
  "Window & Gutter Cleaning",
  "Other / Multiple Services",
];

const PrintableDoc = ({ doc, onClose }) => (
  <div className="print-overlay" data-testid="print-overlay">
    <div className="print-toolbar no-print">
      <button className="btn-quote-nav" onClick={() => window.print()} data-testid="print-doc-button">Print / Save PDF</button>
      <button className="btn-quote-nav" onClick={onClose} data-testid="print-close-button">Close</button>
    </div>
    <div className="print-doc" data-testid="print-doc">
      <div className="pd-header">
        <img src="/assets/logo.webp" alt="Epic Expectations logo" className="pd-logo" />
        <div className="pd-brand">
          <h1>EPIC EXPECTATIONS&trade;</h1>
          <p className="pd-tagline">Battle-Tested Care for Home &amp; Lawn&trade;</p>
          <p>Commanding Officer: Tristian Turmon</p>
          <p>Phone: 470-692-2559</p>
          <p>Email: quote@epicexpectation.info</p>
          <p>www.epicexpectation.com</p>
        </div>
      </div>
      <h2 className="pd-title">{doc.type === "quote" ? "OFFICIAL SERVICE QUOTE" : "CUSTOMER RECEIPT / INVOICE"}</h2>
      <div className="pd-meta">
        <p><strong>{doc.type === "quote" ? "Quote #:" : "Invoice #:"}</strong> {doc.doc_number}</p>
        <p><strong>Date:</strong> {doc.doc_date}</p>
        {doc.type === "invoice" && doc.service_date && <p><strong>Date of Service:</strong> {doc.service_date}</p>}
        {doc.type === "invoice" && doc.technician && <p><strong>Technician(s):</strong> {doc.technician}</p>}
      </div>
      <div className="pd-box">
        <h3>{doc.type === "quote" ? "CLIENT INFORMATION" : "BILL TO / CLIENT INFORMATION"}</h3>
        <p><strong>Name:</strong> {doc.client_name}</p>
        {doc.service_address && <p><strong>Service Address:</strong> {doc.service_address}</p>}
        <div className="pd-row">
          {doc.city_state_zip && <p><strong>City/State/Zip:</strong> {doc.city_state_zip}</p>}
          {doc.phone && <p><strong>Phone:</strong> {doc.phone}</p>}
        </div>
      </div>
      {doc.type === "quote" && doc.services_requested?.length > 0 && (
        <div className="pd-box">
          <h3>ELITE SERVICES REQUESTED</h3>
          <div className="pd-services">
            {SERVICE_CHECKBOXES.map((s) => (
              <span key={s} className="pd-service">
                <span className={`pd-check ${doc.services_requested.includes(s) ? "checked" : ""}`}>{doc.services_requested.includes(s) ? "X" : ""}</span> {s}
              </span>
            ))}
          </div>
        </div>
      )}
      <table className="pd-table">
        <thead>
          <tr>
            <th>{doc.type === "quote" ? "Description of Work / Area to be Serviced" : "Description of Work Completed"}</th>
            <th>{doc.type === "quote" ? "Est. Hours / Sq Ft" : "Qty"}</th>
            <th>{doc.type === "quote" ? "Estimated Total" : "Amount"}</th>
          </tr>
        </thead>
        <tbody>
          {doc.line_items.map((li, i) => (
            <tr key={i}>
              <td>{li.description}</td>
              <td>{li.qty}</td>
              <td>${li.amount.toFixed(2)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="pd-totals">
        <p><strong>Subtotal:</strong> ${doc.subtotal.toFixed(2)}</p>
        <p><strong>Taxes (if applicable):</strong> ${doc.taxes.toFixed(2)}</p>
        <p className="pd-grand"><strong>{doc.type === "quote" ? "TOTAL ESTIMATE:" : "TOTAL AMOUNT:"}</strong> ${doc.total.toFixed(2)}</p>
        {doc.type === "invoice" && <p><strong>Amount Paid:</strong> ${doc.amount_paid.toFixed(2)}</p>}
        {doc.type === "invoice" && <p className="pd-balance"><strong>Balance Due:</strong> ${doc.balance_due.toFixed(2)}</p>}
      </div>
      {doc.type === "invoice" && doc.payment_method && (
        <div className="pd-box"><h3>PAYMENT METHOD</h3><p>{doc.payment_method}</p></div>
      )}
      {doc.type === "invoice" && doc.balance_due > 0 && (
        <div className="pd-box"><h3>PAY ONLINE</h3><p>www.epicexpectation.com/pay/{doc.doc_number} — secure card payment, any time.</p></div>
      )}
      {doc.type === "quote" ? (
        <div className="pd-approval">
          <p><strong>APPROVAL OF QUOTE:</strong> By signing below, you authorize Epic Expectations&trade; to perform the services listed above at the estimated price. Final pricing may vary slightly if the scope of work changes significantly upon arrival, which will always be discussed with you prior to execution of services.</p>
          <div className="pd-sig"><span>Client Signature: ______________________</span><span>Date: ____________</span></div>
        </div>
      ) : (
        <div className="pd-approval pd-thanks">
          <p><strong>MISSION ACCOMPLISHED!</strong></p>
          <p>Thank you for trusting the Epic Squad with your property. We pride ourselves on delivering battle-tested results. The highest compliment you can give us is a 5-star review or a referral to a neighbor!</p>
          <div className="pd-sig"><span>Authorized Signature: ______________________</span></div>
        </div>
      )}
      <p className="pd-footer">Epic Expectations&trade; | {doc.type === "quote" ? "Quote Form" : "Customer Receipt"} | Terms &amp; Guarantee: www.epicexpectation.com/terms</p>
    </div>
  </div>
);

export const DocumentsTab = ({ token }) => {
  const [items, load] = useCollection("/admin/documents", token);
  const [docType, setDocType] = useState("quote");
  const [form, setForm] = useState({ client_name: "", client_email: "", service_address: "", city_state_zip: "", phone: "", technician: "", service_date: "", payment_method: "", taxes: "0", amount_paid: "0" });
  const [services, setServices] = useState([]);
  const [lines, setLines] = useState([{ description: "", qty: "", amount: "" }]);
  const [viewDoc, setViewDoc] = useState(null);
  const [busy, setBusy] = useState(false);

  const issueInvoice = async (id) => {
    await axios.patch(`${API}/admin/documents/${id}`, { status: "issued" }, auth(token));
    load();
  };
  const voidDocument = async (id) => {
    await axios.patch(`${API}/admin/documents/${id}`, { status: "void" }, auth(token));
    load();
  };
  const [recordFor, setRecordFor] = useState(null);
  const [payMethod, setPayMethod] = useState("Cash");
  const [payAmount, setPayAmount] = useState("");
  const recordPayment = async (d) => {
    const amount = parseFloat(payAmount || d.balance_due);
    await axios.post(`${API}/admin/documents/${d.id}/record-payment`, { method: payMethod, amount }, auth(token));
    setRecordFor(null);
    setPayAmount("");
    load();
  };
  const emailInvoice = async (d) => {
    let email = d.client_email || "";
    if (!email) {
      email = window.prompt("Customer email for the pay link:", "") || "";
      if (!email) return;
    }
    try {
      const { data } = await axios.post(`${API}/admin/documents/${d.id}/email`, { origin_url: window.location.origin, email: email || undefined }, auth(token));
      toast.success(`Invoice emailed to ${data.sent_to}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Email failed — copy the pay link instead.");
    }
  };

  const set = (k, v) => setForm({ ...form, [k]: v });
  const setLine = (i, k, v) => setLines(lines.map((l, idx) => (idx === i ? { ...l, [k]: v } : l)));
  const toggleService = (s) => setServices(services.includes(s) ? services.filter((x) => x !== s) : [...services, s]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await axios.post(`${API}/admin/documents`, {
        type: docType,
        ...form,
        taxes: parseFloat(form.taxes) || 0,
        amount_paid: parseFloat(form.amount_paid) || 0,
        services_requested: docType === "quote" ? services : [],
        line_items: lines.filter((l) => l.description && l.amount !== "").map((l) => ({ ...l, amount: parseFloat(l.amount) })),
      }, auth(token));
      setForm({ client_name: "", client_email: "", service_address: "", city_state_zip: "", phone: "", technician: "", service_date: "", payment_method: "", taxes: "0", amount_paid: "0" });
      setServices([]);
      setLines([{ description: "", qty: "", amount: "" }]);
      load();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="documents-tab">
      <div className="doc-type-toggle">
        <button className={docType === "quote" ? "active" : ""} onClick={() => setDocType("quote")} data-testid="doc-type-quote">Service Quote</button>
        <button className={docType === "invoice" ? "active" : ""} onClick={() => setDocType("invoice")} data-testid="doc-type-invoice">Receipt / Invoice</button>
      </div>
      <form className="ops-form doc-form" onSubmit={submit} data-testid="document-form">
        <input placeholder="Client Name" required value={form.client_name} onChange={(e) => set("client_name", e.target.value)} data-testid="doc-client-name" />
        <input type="email" placeholder="Client Email (for pay link)" value={form.client_email} onChange={(e) => set("client_email", e.target.value)} data-testid="doc-client-email" />
        <input placeholder="Service Address" value={form.service_address} onChange={(e) => set("service_address", e.target.value)} data-testid="doc-address" />
        <input placeholder="City/State/Zip" value={form.city_state_zip} onChange={(e) => set("city_state_zip", e.target.value)} data-testid="doc-city" />
        <input placeholder="Phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} data-testid="doc-phone" />
        {docType === "invoice" && (
          <>
            <input placeholder="Technician(s)" value={form.technician} onChange={(e) => set("technician", e.target.value)} data-testid="doc-technician" />
            <input type="date" aria-label="Date of service" value={form.service_date} onChange={(e) => set("service_date", e.target.value)} data-testid="doc-service-date" />
            <select value={form.payment_method} onChange={(e) => set("payment_method", e.target.value)} data-testid="doc-payment-method">
              <option value="">Payment Method...</option>
              <option value="Cash">Cash</option>
              <option value="Check">Check</option>
              <option value="Credit / Debit Card">Credit / Debit Card</option>
              <option value="Digital (Zelle / Venmo)">Digital (Zelle / Venmo)</option>
            </select>
            <input type="number" step="0.01" placeholder="Amount Paid ($)" value={form.amount_paid} onChange={(e) => set("amount_paid", e.target.value)} data-testid="doc-amount-paid" />
          </>
        )}
        {docType === "quote" && (
          <div className="doc-services-pick" data-testid="doc-services-pick">
            {SERVICE_CHECKBOXES.map((s) => (
              <button type="button" key={s} className={`onboard-step ${services.includes(s) ? "done" : ""}`} onClick={() => toggleService(s)} data-testid={`doc-service-${s.replace(/[^a-z]/gi, "-").toLowerCase()}`}>
                {s}
              </button>
            ))}
          </div>
        )}
        <div className="doc-lines">
          {lines.map((l, i) => (
            <div className="doc-line" key={i}>
              <input placeholder={docType === "quote" ? "Description of work / area" : "Description of work completed"} required value={l.description} onChange={(e) => setLine(i, "description", e.target.value)} data-testid={`doc-line-desc-${i}`} />
              <input placeholder={docType === "quote" ? "Est. hrs / sq ft" : "Qty"} value={l.qty} onChange={(e) => setLine(i, "qty", e.target.value)} data-testid={`doc-line-qty-${i}`} />
              <input type="number" step="0.01" placeholder="Amount ($)" required value={l.amount} onChange={(e) => setLine(i, "amount", e.target.value)} data-testid={`doc-line-amount-${i}`} />
              {lines.length > 1 && <button type="button" className="doc-line-remove" onClick={() => setLines(lines.filter((_, idx) => idx !== i))} data-testid={`doc-line-remove-${i}`}>x</button>}
            </div>
          ))}
          {lines.length < 20 && <button type="button" className="btn-quote-nav admin-action" onClick={() => setLines([...lines, { description: "", qty: "", amount: "" }])} data-testid="doc-add-line">+ Add Line</button>}
        </div>
        <input type="number" step="0.01" placeholder="Taxes ($)" value={form.taxes} onChange={(e) => set("taxes", e.target.value)} data-testid="doc-taxes" />
        <button type="submit" className="submit-btn ops-submit" disabled={busy} data-testid="doc-submit">{busy ? "CREATING..." : `CREATE ${docType === "quote" ? "QUOTE" : "INVOICE"}`}</button>
      </form>
      <div className="admin-leads">
        {items.map((d) => (
          <div className="lead-card" key={d.id} data-testid={`document-card-${d.id}`}>
            <div className="lead-top">
              <span className="lead-service">{d.doc_number}</span>
              <StatusPill
                label={d.type === "quote" ? "QUOTE" : ((d.balance_due ?? 0) <= 0 && (d.amount_paid || 0) > 0 ? "PAID" : d.status === "issued" ? "ISSUED" : (d.status || "draft").toUpperCase())}
                tone={d.type === "quote" ? "blue" : ((d.balance_due ?? 0) <= 0 && (d.amount_paid || 0) > 0) || d.status === "issued" ? "green" : "pink"}
              />
            </div>
            <h3>{d.client_name}</h3>
            <p className="ops-amount"><DollarSign size={16} /> {d.total.toFixed(2)}{d.type === "invoice" && d.balance_due > 0 ? ` · due $${d.balance_due.toFixed(2)}` : ""}</p>
            <p className="lead-details">{d.doc_date}</p>
            <div className="ops-actions">
              <button onClick={() => setViewDoc(d)} data-testid={`document-view-${d.id}`}>View / Print</button>
              {d.type === "invoice" && (d.status || "draft") === "draft" && d.balance_due > 0 && (
                <button onClick={() => issueInvoice(d.id)} data-testid={`document-issue-${d.id}`}>Job Complete — Issue for Payment</button>
              )}
              {d.type === "invoice" && d.status === "issued" && d.balance_due > 0 && (
                <button onClick={() => { navigator.clipboard?.writeText(`${window.location.origin}/pay/${d.doc_number}`); }} data-testid={`document-paylink-${d.id}`}>Copy Pay Link</button>
              )}
              {d.type === "invoice" && d.status === "issued" && d.balance_due > 0 && (
                <button onClick={() => emailInvoice(d)} data-testid={`document-email-${d.id}`}>Email Pay Link</button>
              )}
              {d.type === "invoice" && d.balance_due > 0 && (d.status || "draft") !== "void" && (
                <button onClick={() => { setRecordFor(recordFor === d.id ? null : d.id); setPayAmount(String(d.balance_due)); }} data-testid={`document-record-${d.id}`}>Record Cash/Check</button>
              )}
              {(d.status || "draft") !== "void" && d.balance_due > 0 && (
                <button onClick={() => voidDocument(d.id)} data-testid={`document-void-${d.id}`}>Void</button>
              )}
            </div>
            {recordFor === d.id && (
              <div className="record-pay" data-testid={`record-pay-${d.id}`}>
                <select value={payMethod} onChange={(e) => setPayMethod(e.target.value)} data-testid={`record-method-${d.id}`}>
                  <option value="Cash">Cash</option>
                  <option value="Check">Check</option>
                  <option value="Credit / Debit Card (in person)">Credit / Debit Card (in person)</option>
                  <option value="Digital (Zelle / Venmo)">Digital (Zelle / Venmo)</option>
                </select>
                <input type="number" step="0.01" value={payAmount} onChange={(e) => setPayAmount(e.target.value)} placeholder="Amount ($)" data-testid={`record-amount-${d.id}`} />
                <button onClick={() => recordPayment(d)} data-testid={`record-confirm-${d.id}`}>Confirm Payment</button>
              </div>
            )}
            {d.payments?.length > 0 && (
              <p className="lead-details" data-testid={`payments-log-${d.id}`}>
                Payments: {d.payments.map((p) => `${p.method} $${p.amount.toFixed(2)}`).join(" · ")}
              </p>
            )}
          </div>
        ))}
      </div>
      {viewDoc && <PrintableDoc doc={viewDoc} onClose={() => setViewDoc(null)} />}
    </div>
  );
};
